import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaWifi } from 'react-icons/fa';
import { Modal, Button } from 'react-bootstrap';
import './Pagos.css';
import deUnaLogo from '../assets/deuna.png';
import payphoneLogo from '../assets/payphone.png';
import subirDepositoLogo from '../assets/subirdeposito.png';
import DeunaClienteModal from '../components/deuna_cliente';
import PayphoneClienteModal from '../components/payphone_cliente';
import DepositoClienteModal from '../components/deposito_cliente';
import AppNavbar from '../components/AppNavbar';
import BottomNav from '../components/BottomNav';
import axios from 'axios';

const N8N_CLIENTE  = process.env.REACT_APP_N8N_URL_CLIENTE_CEDULA!;
const N8N_FACTURAS = process.env.REACT_APP_N8N_URL_FACTURAS_CLIENTE!;

// Texto secundario sobre la tarjeta grafito de cabecera
const TEXTO_HERO = 'var(--sn-texto-invertido)';

/* Insignias de las redes de tarjetas. Colores oficiales de marcas EXTERNAS,
   no de SisNetel: se quedan tal cual en ambos temas (ver nota en el render). */
const MARCAS_TARJETA = [
  { nombre: 'VISA',       color: '#1a1f71' },
  { nombre: 'MASTERCARD', color: '#eb001b' },
  { nombre: 'AMEX',       color: '#007bc1' },
  { nombre: 'DINERS',     color: '#004a97' },
] as const;

// Parser para el campo "smartolt", que viene como cadena PHP-serialized (a:17:{s:6:"olt_id";s:1:"9";...})
const parsePhpSerialized = (raw: string): Record<string, string> => {
  const result: Record<string, string> = {};
  if (!raw || typeof raw !== 'string') return result;
  const re = /s:\d+:"([^"]*)"/g;
  let m: RegExpExecArray | null;
  let lastKey = '';
  let idx = 0;
  while ((m = re.exec(raw)) !== null) {
    if (idx % 2 === 0) lastKey = m[1];
    else if (lastKey) { result[lastKey] = m[1]; lastKey = ''; }
    idx++;
  }
  return result;
};

const Pagos: React.FC = () => {
  const { cedula64 } = useParams();
  const navigate = useNavigate();
  const [clientData, setClientData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetodo, setSelectedMetodo] = useState<string | null>(null);
  const [showDeuna, setShowDeuna] = useState(false);
  const [showPayphone, setShowPayphone] = useState(false);
  const [showDeposito, setShowDeposito] = useState(false);
  const [showMinModal, setShowMinModal] = useState(false);
  const [facturaIdPendiente, setFacturaIdPendiente] = useState<string | undefined>(undefined);
  const [facturaVencimiento, setFacturaVencimiento] = useState<string | undefined>(undefined);
  const [ultimaFactura, setUltimaFactura] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [cuentaCopiada, setCuentaCopiada] = useState<string | null>(null);

  const copiarCuenta = (banco: string, cuenta: string) => {
    navigator.clipboard?.writeText(cuenta.replace(/\s/g, '')).then(() => {
      setCuentaCopiada(banco);
      setTimeout(() => setCuentaCopiada(null), 2000);
    });
  };

  const recargarCliente = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  useEffect(() => {
    if (!cedula64) return;
    try {
      const cedula = atob(cedula64);
      axios.post(N8N_CLIENTE, { cedula })
        .then((res) => {
          const datos = res.data?.datos?.[0];
          if (datos) {
            setClientData(datos);
            if (datos.id) {
              // Factura pendiente
              axios.post(N8N_FACTURAS, { idcliente: datos.id, estado: '1', limit: 1 })
                .then((r) => {
                  const facturas = r.data?.facturas || [];
                  if (facturas.length > 0) {
                    setFacturaIdPendiente(String(facturas[0].id));
                    setFacturaVencimiento(facturas[0].vencimiento || facturas[0].fecha_vencimiento || undefined);
                  }
                })
                .catch(() => {});

              // Última factura pagada
              axios.post(N8N_FACTURAS, { idcliente: datos.id, estado: '0', limit: 1 })
                .then((r) => {
                  const facturas = r.data?.facturas || [];
                  if (facturas.length > 0) setUltimaFactura(facturas[0]);
                })
                .catch(() => {});
            }
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } catch {
      setLoading(false);
    }
  }, [cedula64, refreshKey]);

  if (loading) return (
    /* Pantalla de carga a alto completo. Llevaba bg-white de Bootstrap, que es
       un #fff fijo: en tema oscuro era una pantalla entera en blanco antes de
       cada consulta. El fondo va al lienzo del tema. */
    <div
      className="d-flex flex-column justify-content-center align-items-center vh-100"
      style={{ textAlign: 'center', background: 'var(--sn-fondo)' }}
    >
      <img src="/sisnetelclaro.png" alt="Sisnetel Fiber" className="logo-cargando" style={{ width: '180px', marginBottom: '20px' }} />
      <p className="mt-4 fs-5" style={{ color: 'var(--sn-texto-2)' }}>Sincronizando tu información...</p>
    </div>
  );

  if (!clientData) {
    return (
      <div className="container mt-5">
        <h4>❌ No se encontró información del cliente.</h4>
        <button className="btn btn-primary mt-3" onClick={() => navigate('/')}>Volver al Inicio</button>
      </div>
    );
  }

  const servicios = clientData.servicios || [];
  const totalFacturas = clientData.facturacion?.total_facturas || '0.00';
  const estadoServicio = clientData.estado?.toUpperCase() || 'DESCONOCIDO';
  const monto = parseFloat(totalFacturas);
  const primerNombre = clientData?.nombre?.split(' ')[0] || 'Cliente';
  const saldoCero = monto === 0;

  const handlePagoClick = async (metodo: 'deuna' | 'payphone' | 'deposito') => {
    if (metodo === 'payphone' && monto < 5) { setShowMinModal(true); return; }
    setSelectedMetodo(metodo);
    if (metodo === 'deuna') setShowDeuna(true);
    if (metodo === 'deposito') setShowDeposito(true);
    if (metodo === 'payphone') {
      try {
        await fetch('https://nuevo-n8n.u5r75b.easypanel.host/webhook/sisnetel/payphone/pagos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            idcliente: clientData.id,
            monto: Math.round(monto * 100),
            cedula: clientData.cedula,
          }),
        });
      } catch {}
      setShowPayphone(true);
    }
  };

  const verFactura = (url: string) => {
    if (url) window.open(url, '_system');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: 'var(--sn-fondo)' }}>
      <AppNavbar />

      <div className="container pb-5" style={{ maxWidth: 480, flex: 1, overflowY: 'auto' as const }}>

        {/* Botón Atrás */}
        <div className="pt-3 pb-1">
          <button
            onClick={() => navigate('/menu')}
            style={{ background: 'none', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--sn-marca)', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', padding: '4px 0' }}
          >
            <FaArrowLeft size={14} />
            Atrás
          </button>
        </div>

        {/* Tarjeta grafito compacta: 2 líneas */}
        <div style={{ background: 'linear-gradient(135deg, var(--sn-info-texto) 0%, var(--sn-info) 100%)', borderRadius: 14, padding: '10px 14px', marginBottom: 16, boxShadow: 'var(--sn-sombra)', color: 'var(--sn-texto-invertido)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{clientData.nombre}</span>
            <span style={{ flexShrink: 0, padding: '1px 8px', borderRadius: 20, background: estadoServicio === 'ACTIVO' ? 'var(--sn-exito)' : 'var(--sn-peligro)', color: 'var(--sn-texto-invertido)', fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.5 }}>
              {estadoServicio}
            </span>
          </div>
          <div style={{ marginTop: 6, fontSize: '0.72rem', color: TEXTO_HERO, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Cédula <strong style={{ color: 'var(--sn-texto-invertido)' }}>{clientData.cedula}</strong>
            {' · '}Factura <strong style={{ color: 'var(--sn-texto-invertido)' }}>#{facturaIdPendiente || '—'}</strong>
            {facturaVencimiento && <> · Vence <strong style={{ color: 'var(--sn-texto-invertido)' }}>{facturaVencimiento}</strong></>}
          </div>
        </div>

        {!saldoCero && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--sn-texto-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 6 }}>
              Adeuda
            </span>
            <span style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--sn-peligro)' }}>${monto.toFixed(2)}</span>
          </div>
        )}

        {/* Servicios */}
        {servicios.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ color: 'var(--sn-texto-2)', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Servicios</div>
            {servicios.map((s: any, i: number) => {
              const smartolt = parsePhpSerialized(s.smartolt || '');
              return (
                <div key={i} style={{ background: 'var(--sn-info-suave)', borderRadius: 12, padding: '10px 14px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--sn-info-texto)', fontWeight: 500, fontSize: '0.9rem' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: 'var(--sn-texto-invertido)', background: 'var(--sn-info)',
                        borderRadius: 999, width: 16, height: 16, display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        {i + 1}
                      </span>
                      <FaWifi size={14} />{s.perfil}
                    </span>
                    <span style={{ fontWeight: 700, color: 'var(--sn-info-texto)', fontSize: '0.9rem' }}>${s.costo}</span>
                  </div>
                  {(smartolt.olt_id || smartolt.sn) && (
                    <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: '0.72rem', color: 'var(--sn-texto-2)' }}>
                      {smartolt.olt_id && <span><strong>OLT ID:</strong> {smartolt.olt_id}</span>}
                      {smartolt.sn && <span><strong>SN:</strong> {smartolt.sn}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── SALDO CERO ── */}
        {saldoCero ? (
          <>
            {/* Mensaje opción B */}
            <div style={{ background: 'var(--sn-exito-suave)', border: '0.5px solid var(--sn-exito)', borderRadius: 12, padding: '12px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--sn-superficie)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: 20 }}>✅</span>
              </div>
              <div>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--sn-exito-texto)', marginBottom: 2 }}>¡Estás al día, {primerNombre}!</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--sn-exito-texto)', lineHeight: 1.5 }}>Tu cuenta no tiene deuda pendiente.<br />Gracias por tu puntualidad con Sisnetel.</div>
              </div>
            </div>

            {/* Última factura con detalles */}
            {ultimaFactura && (
              <>
                <div style={{ color: 'var(--sn-texto-2)', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Última factura</div>
                <div style={{ background: 'var(--sn-superficie)', borderRadius: 14, border: '0.5px solid var(--sn-borde)', padding: '14px 16px' }}>
                  {[
                    { k: 'Número', v: `#${ultimaFactura.id}` },
                    { k: 'Emitida', v: ultimaFactura.emitido || ultimaFactura.fecha_emision || '—' },
                    { k: 'Vencimiento', v: ultimaFactura.vencimiento || ultimaFactura.fecha_vencimiento || '—' },
                    { k: 'Monto', v: `$${parseFloat(ultimaFactura.total || ultimaFactura.monto || 0).toFixed(2)}` },
                  ].map(({ k, v }) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '0.5px solid var(--sn-borde)', fontSize: '0.85rem' }}>
                      <span style={{ color: 'var(--sn-texto-3)' }}>{k}</span>
                      <span style={{ fontWeight: 600, color: 'var(--sn-texto)' }}>{v}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '0.85rem' }}>
                    <span style={{ color: 'var(--sn-texto-3)' }}>Estado</span>
                    <span style={{ background: 'var(--sn-exito-suave)', color: 'var(--sn-exito-texto)', fontSize: '0.75rem', padding: '2px 10px', borderRadius: 20, fontWeight: 600 }}>PAGADA</span>
                  </div>
                  {ultimaFactura.urlpdf && (
                    <button
                      onClick={() => verFactura(ultimaFactura.urlpdf)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', marginTop: 12, background: 'var(--sn-accion)', color: 'var(--sn-accion-texto)', border: 'none', borderRadius: 10, padding: '10px 0', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      📄 Ver última factura
                    </button>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          /* ── CON DEUDA ── */
          <>
            {/* Botones de pago */}
            <div style={{ color: 'var(--sn-texto-2)', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, textAlign: 'center' }}>
              Selecciona un método de pago
            </div>
            <div className="botones-pago" style={{ marginBottom: 20 }}>
              <button className={`metodo-icono ${selectedMetodo === 'deuna' ? 'selected' : ''}`} onClick={() => handlePagoClick('deuna')}>
                <img src={deUnaLogo} alt="Banco Pichincha" />
                <span>Banco<br />Pichincha</span>
              </button>
              <button className={`metodo-icono ${selectedMetodo === 'payphone' ? 'selected' : ''}`} onClick={() => handlePagoClick('payphone')}>
                <img src={payphoneLogo} alt="PayPhone" />
                <span>Tarjetas<br />Crédito</span>
              </button>
              <button className={`metodo-icono ${selectedMetodo === 'deposito' ? 'selected' : ''}`} onClick={() => handlePagoClick('deposito')}>
                <img src={subirDepositoLogo} alt="Subir Depósito" />
                <span>Subir<br />Depósito</span>
              </button>
            </div>

            {/* Última factura pagada + botón Ver */}
            {ultimaFactura && (
              <div style={{ background: 'var(--sn-superficie)', borderRadius: 12, border: '0.5px solid var(--sn-borde)', padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--sn-texto)' }}>Última factura pagada</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--sn-texto-3)', marginTop: 2 }}>
                    #{ultimaFactura.id} · ${parseFloat(ultimaFactura.total || ultimaFactura.monto || 0).toFixed(2)} · {ultimaFactura.emitido || ultimaFactura.fecha_emision || ''}
                  </div>
                </div>
                {ultimaFactura.urlpdf && (
                  <button
                    onClick={() => verFactura(ultimaFactura.urlpdf)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--sn-accion)', color: 'var(--sn-accion-texto)', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    📄 Ver
                  </button>
                )}
              </div>
            )}

            {/* Divisor detalles y comisiones */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1, height: '0.5px', background: 'var(--sn-borde)' }} />
              <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Detalles y comisiones</span>
              <div style={{ flex: 1, height: '0.5px', background: 'var(--sn-borde)' }} />
            </div>

            {/* Tarjetas info horizontales */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {/* Pichincha */}
              <div style={{ background: 'var(--sn-info-suave)', border: '0.5px solid var(--sn-borde)', borderRadius: 10, padding: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 16, marginBottom: 3 }}>🏦</div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--sn-info-texto)', lineHeight: 1.3, marginBottom: 3 }}>Banco Pichincha</div>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--sn-texto)' }}>${monto.toFixed(2)}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--sn-texto-2)', marginTop: 2 }}>Sin comisión</div>
              </div>

              {/* Tarjetas */}
              <div style={{ background: 'var(--sn-alerta-suave)', border: '0.5px solid var(--sn-alerta)', borderRadius: 10, padding: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 16, marginBottom: 3 }}>💳</div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--sn-alerta-texto)', lineHeight: 1.3, marginBottom: 3 }}>Tarjetas crédito</div>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--sn-alerta-texto)' }}>${monto.toFixed(2)}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--sn-alerta-texto)', marginTop: 2 }}>Mín. $5.00</div>
                {/* EXCEPCION a "todo color sale de var(--sn-*)": estos son los colores
                    oficiales de marcas EXTERNAS (redes de tarjetas), igual que el verde
                    de WhatsApp en AyudaModal. No son azul de nuestra identidad: el
                    cliente reconoce el medio de pago por ellos y repintarlos de grafito
                    los volveria irreconocibles.
                    El texto va en '#fff' literal, NO en --sn-texto-invertido: el fondo
                    de la insignia es fijo en ambos temas, asi que en tema oscuro el
                    token daria texto oscuro sobre el azul marino de Visa. */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 2, marginTop: 5, flexWrap: 'wrap' }}>
                  {MARCAS_TARJETA.map(({ nombre, color }) => (
                    <span key={nombre} style={{ fontSize: '0.55rem', fontWeight: 700, padding: '1px 3px', borderRadius: 3, color: '#fff', background: color }}>{nombre}</span>
                  ))}
                </div>
              </div>

              {/* Depósito */}
              <div style={{ background: 'var(--sn-exito-suave)', border: '0.5px solid var(--sn-exito)', borderRadius: 10, padding: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 16, marginBottom: 3 }}>📤</div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--sn-exito-texto)', lineHeight: 1.3, marginBottom: 3 }}>Subir depósito</div>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--sn-exito-texto)' }}>${monto.toFixed(2)}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--sn-exito-texto)', marginTop: 2 }}>Sin comisión</div>
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: '0.5px solid var(--sn-exito)', textAlign: 'left' as const }}>
                  <div style={{ fontSize: '0.56rem', color: 'var(--sn-exito-texto)', opacity: 0.75, marginBottom: 2 }}>
                    Ctas. corrientes — SISNETEL FIBER EC
                  </div>
                  {[
                    { banco: 'Pichincha', cuenta: '2100 310 307' },
                    { banco: 'Guayaquil', cuenta: '35 835 490' },
                    { banco: 'Produbanco', cuenta: '2006191599' },
                  ].map((b) => (
                    <div key={b.banco} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.58rem', color: 'var(--sn-exito-texto)', lineHeight: 1.6 }}>
                      <span>
                        <span style={{ opacity: 0.8 }}>{b.banco}:</span> <strong>{b.cuenta}</strong>
                      </span>
                      <button
                        type="button"
                        onClick={() => copiarCuenta(b.banco, b.cuenta)}
                        style={{
                          fontSize: '0.55rem', fontWeight: 700, color: 'var(--sn-exito-texto)', background: 'var(--sn-superficie)',
                          border: '1px solid var(--sn-exito)', borderRadius: 999, padding: '1px 5px', cursor: 'pointer',
                        }}
                      >
                        {cuentaCopiada === b.banco ? '✓' : '📋'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <BottomNav />

      {/* Modal tarjeta mínimo $5 */}
      <Modal show={showMinModal} onHide={() => setShowMinModal(false)} centered>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: 64 }}>😔</div>
          <h4 className="mt-3 fw-bold">¡Hola, {primerNombre}!</h4>
          <p className="text-muted mt-2">
            El monto mínimo para pagar con <strong>Tarjeta de Crédito</strong> es <strong>$5.00</strong>.<br /><br />
            Tu valor actual es <strong className="text-danger">${monto.toFixed(2)}</strong>.<br /><br />
            Te recomendamos usar <strong>Banco Pichincha</strong> o <strong>Subir Depósito</strong>.
          </p>
          <Button variant="primary" className="mt-3 px-4" onClick={() => setShowMinModal(false)}>Entendido</Button>
        </Modal.Body>
      </Modal>

      {/* Modales de pago */}
      <DeunaClienteModal
        show={showDeuna}
        onHide={() => setShowDeuna(false)}
        monto={monto}
        cedula={clientData.cedula}
        nombre={clientData.nombre}
        estado={clientData.estado}
        facturaId={facturaIdPendiente}
      />
      <PayphoneClienteModal
        show={showPayphone}
        onHide={() => setShowPayphone(false)}
        monto={monto}
        cedula={clientData.cedula}
        nombre={clientData.nombre}
        estado={clientData.estado}
        facturaId={facturaIdPendiente}
        clienteId={String(clientData.id)}
        onBrowserClose={recargarCliente}
      />
      <DepositoClienteModal
        show={showDeposito}
        onHide={() => setShowDeposito(false)}
        clienteNombre={clientData.nombre}
        clienteEstado={clientData.estado}
        cedula={clientData.cedula}
      />
    </div>
  );
};

export default Pagos;
